import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CommandeList from '../components/achat/commande/CommandeList';
import SingleCommande from '../components/achat/commande/SingleCommande';
import UnsavedChangesGuard from '../utils/UnsavedChangesGuard';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';

const AchatCommandePage = () => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const containerRef = useRef(null);

  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
    isDevisManager: user?.EffectiveIsDevisManager ?? user?.IsDevisManager,
  };

  // Handler for when changes are detected in SingleCommande
  const handleChangesDetected = () => {
    setHasUnsavedChanges(true);
  };

  // Handler for when changes are saved in SingleCommande
  const handleChangesSaved = () => {
    setHasUnsavedChanges(false);
  };

  // Handler to check for unsaved changes before view change
  const handleViewChange = useCallback((newView) => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Modif. non enregistrées',
        content: 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter sans sauvegarder ?',
        okText: 'Quitter',
        cancelText: 'Rester',
        onOk: () => {
          requestAnimationFrame(() => {
            setCurrentView(newView);
            setHasUnsavedChanges(false);
          });
        },
      });
    } else {
      requestAnimationFrame(() => {
        setCurrentView(newView);
      });
    }
  }, [hasUnsavedChanges]);

  const handleSelect = (id) => {
    setSelectedId(id);
    handleViewChange('single');
  };

  const handleClose = () => {
    setSelectedId(null);
    handleViewChange('list');
  };

  const handleConfirmNavigation = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  // Optimized animation variants
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.97,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0],
      }
    }
  };

  // Function to render the current view
  const renderView = () => {
    switch (currentView) {
      case 'list':
        return (
          <CommandeList
            onSelect={handleSelect}
            userPermissions={userPermissions}
          />
        );
      case 'single':
        return selectedId && (
          <SingleCommande
            id={selectedId}
            onClose={handleClose}
            userPermissions={userPermissions}
            onChangesSaved={handleChangesSaved}
            onChangesDetected={handleChangesDetected}
          />
        );
      default:
        return null;
    }
  };

  return (
    <UnsavedChangesGuard
      hasUnsavedChanges={hasUnsavedChanges}
      onConfirmNavigation={handleConfirmNavigation}
    >
      <div className="commande-management-container" ref={containerRef}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pageVariants}
            className="view-container"
            style={{
              width: '100%',
              height: '100%'
            }}
          >
            {renderView()}
          </motion.div>
        </AnimatePresence>
      </div>
    </UnsavedChangesGuard>
  );
};

export default AchatCommandePage;
